import { useEffect, useRef } from 'react';

import { useAccountIdentity, useAccountToken } from '@/features/account';
import { saveAccountSyncedSourceAuth } from '@/features/account/accountSourceAuth';
import { getAllSources } from '@/sources/registry';

/**
 * Persists session cookies that sources refresh on their own (see
 * `ComicSource.onAuthRefreshed`), so a healed ExHentai `igneous` survives
 * reloads instead of being re-fetched every session. Renders nothing.
 */
export function SourceAuthRefreshPersister() {
  const getAccountToken = useAccountToken();
  const accountIdentity = useAccountIdentity();
  const currentAccountIdentity = useRef(accountIdentity);
  currentAccountIdentity.current = accountIdentity;

  useEffect(() => {
    if (!accountIdentity) return;
    const unsubscribers = getAllSources().map((source) =>
      source.onAuthRefreshed?.((auth) => {
        void (async () => {
          const token = await getAccountToken();
          if (!token || currentAccountIdentity.current !== accountIdentity) return;
          await saveAccountSyncedSourceAuth(
            source.id,
            auth,
            token,
            accountIdentity,
            () => currentAccountIdentity.current,
          );
        })().catch(() => undefined);
      }),
    );
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe?.();
    };
  }, [accountIdentity, getAccountToken]);

  return null;
}
